import { useState, useEffect } from "react";
import { 
  Users, TrendingUp, TrendingDown, Package, Building2, 
  Activity, BarChart3, Euro, AlertTriangle, Bell,
  Send, RefreshCw, ChevronRight, Globe, Link2, Unlink
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2 
  }).format(value || 0);
};

export default function AdminDashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [ricambistiPerf, setRicambistiPerf] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastData, setBroadcastData] = useState({
    title: "",
    message: "",
    target_type: "all"
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, perfRes, trendRes] = await Promise.all([
        authFetch(`${API}/admin/network/stats`, { }),
        authFetch(`${API}/admin/network/ricambisti-performance`, { }),
        authFetch(`${API}/admin/network/monthly-trend`, { })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (perfRes.ok) setRicambistiPerf(await perfRes.json());
      if (trendRes.ok) setMonthlyTrend(await trendRes.json());
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastData.title.trim() || !broadcastData.message.trim()) {
      toast.error("Compila tutti i campi");
      return;
    }
    setSending(true);
    try {
      const res = await authFetch(`${API}/admin/network/broadcast`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(broadcastData)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowBroadcastDialog(false);
        setBroadcastData({ title: "", message: "", target_type: "all" });
      } else {
        toast.error("Errore nell'invio");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  const maxRevenue = Math.max(...monthlyTrend.map(m => m.revenue), 1);

  return (
    <div data-testid="admin-dashboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <Globe className="w-8 h-8 text-primary" />
            Dashboard Network ADIM
          </h1>
          <p className="text-zinc-500 mt-1">Monitoraggio strategico della rete</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchDashboardData}
            data-testid="refresh-dashboard"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
          <Button 
            onClick={() => setShowBroadcastDialog(true)}
            data-testid="broadcast-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Broadcast
          </Button>
        </div>
      </div>

      {/* Network KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="card p-4 border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between mb-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            <span className="text-xs text-zinc-500">Ricambisti</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats?.network?.total_ricambisti || 0}</div>
          <div className="text-xs text-green-400">{stats?.network?.active_ricambisti || 0} attivi</div>
        </div>

        <div className="card p-4 border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-purple-400" />
            <span className="text-xs text-zinc-500">Officine</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats?.network?.total_officine || 0}</div>
          <div className="text-xs text-green-400">{stats?.network?.active_officine || 0} attive</div>
        </div>

        <div className="card p-4 border-l-4 border-l-green-500">
          <div className="flex items-center justify-between mb-2">
            <Link2 className="w-5 h-5 text-green-400" />
            <span className="text-xs text-zinc-500">Collegate</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats?.network?.linked_officine || 0}</div>
          <div className="text-xs text-primary">{stats?.network?.coverage_rate || 0}% copertura</div>
        </div>

        <div className="card p-4 border-l-4 border-l-yellow-500">
          <div className="flex items-center justify-between mb-2">
            <Unlink className="w-5 h-5 text-yellow-400" />
            <span className="text-xs text-zinc-500">Da Collegare</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats?.network?.unlinked_officine || 0}</div>
          {stats?.network?.unlinked_officine > 0 && (
            <div className="text-xs text-yellow-400">Azione richiesta</div>
          )}
        </div>

        <div className="card p-4 border-l-4 border-l-primary">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-5 h-5 text-primary" />
            <span className="text-xs text-zinc-500">Ordini Mese</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats?.orders?.monthly || 0}</div>
          <div className={`text-xs flex items-center gap-1 ${stats?.orders?.monthly_trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {stats?.orders?.monthly_trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {stats?.orders?.monthly_trend > 0 ? '+' : ''}{stats?.orders?.monthly_trend || 0}%
          </div>
        </div>

        <div className="card p-4 border-l-4 border-l-red-500">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-xs text-zinc-500">Urgenti</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats?.requests?.urgent || 0}</div>
          <div className="text-xs text-zinc-400">{stats?.requests?.pending || 0} in attesa</div>
        </div>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-6 bg-gradient-to-br from-primary/20 to-transparent">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
              <Euro className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-zinc-400 text-sm">Fatturato Mensile</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.monthly)}</p>
            </div>
          </div>
          <div className={`flex items-center gap-2 ${stats?.revenue?.monthly_trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {stats?.revenue?.monthly_trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span>{stats?.revenue?.monthly_trend > 0 ? '+' : ''}{stats?.revenue?.monthly_trend || 0}% vs mese precedente</span>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-sm flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-sm">Fatturato Annuale</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.yearly)}</p>
            </div>
          </div>
          <div className="text-zinc-500 text-sm">
            {stats?.orders?.yearly || 0} ordini totali
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-sm flex items-center justify-center">
              <Activity className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-sm">Media per Officina</p>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(stats?.network?.linked_officine > 0 ? stats?.revenue?.monthly / stats?.network?.linked_officine : 0)}
              </p>
            </div>
          </div>
          <div className="text-zinc-500 text-sm">
            basata su {stats?.network?.linked_officine || 0} officine attive
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Trend Ultimi 6 Mesi
          </h3>
          <div className="space-y-3">
            {monthlyTrend.map((month, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-16 text-xs text-zinc-400">{month.month}</div>
                <div className="flex-1 relative">
                  <div className="h-6 bg-zinc-800 rounded-sm overflow-hidden">
                    <div 
                      className="h-full bg-primary/60 rounded-sm transition-all"
                      style={{ width: `${(month.revenue / maxRevenue) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="w-24 text-right text-sm text-white font-mono">
                  {formatCurrency(month.revenue)}
                </div>
                <div className="w-12 text-right text-xs text-zinc-400">
                  {month.orders} ord.
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Ricambisti */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Performance Ricambisti
            </h3>
            <a href="/admin/ricambisti" className="text-primary text-sm hover:underline flex items-center gap-1">
              Gestisci <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {ricambistiPerf.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nessun ricambista registrato</p>
              </div>
            ) : (
              ricambistiPerf.map((ric, idx) => (
                <div key={ric.id} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-sm hover:bg-zinc-800 transition-colors">
                  <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-sm font-bold ${idx < 3 ? 'bg-primary/20 text-primary' : 'bg-zinc-700 text-zinc-400'}`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{ric.company_name}</p>
                    <p className="text-zinc-500 text-xs">{ric.city} • {ric.officine_count} officine</p>
                  </div>
                  <div className="text-right">
                    <p className="text-primary font-mono text-sm">{formatCurrency(ric.monthly_revenue)}</p>
                    <p className="text-zinc-500 text-xs">{ric.monthly_orders} ordini</p>
                  </div>
                  {ric.pending_requests > 0 && (
                    <div className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                      {ric.pending_requests} pending
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Gestione Rapida</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a href="/admin/ricambisti" className="p-4 bg-zinc-800/50 rounded-sm hover:bg-zinc-800 transition-colors border border-zinc-700 hover:border-primary/50">
            <Building2 className="w-6 h-6 text-primary mb-2" />
            <h4 className="text-white font-medium">Ricambisti & Officine</h4>
            <p className="text-zinc-500 text-sm">Gestisci collegamenti</p>
          </a>
          <a href="/admin/users" className="p-4 bg-zinc-800/50 rounded-sm hover:bg-zinc-800 transition-colors border border-zinc-700 hover:border-primary/50">
            <Users className="w-6 h-6 text-primary mb-2" />
            <h4 className="text-white font-medium">Utenti</h4>
            <p className="text-zinc-500 text-sm">Ruoli e permessi</p>
          </a>
          <a href="/admin/campaigns" className="p-4 bg-zinc-800/50 rounded-sm hover:bg-zinc-800 transition-colors border border-zinc-700 hover:border-primary/50">
            <Activity className="w-6 h-6 text-primary mb-2" />
            <h4 className="text-white font-medium">Campagne</h4>
            <p className="text-zinc-500 text-sm">Promo e punti</p>
          </a>
          <a href="/admin/audit" className="p-4 bg-zinc-800/50 rounded-sm hover:bg-zinc-800 transition-colors border border-zinc-700 hover:border-primary/50">
            <BarChart3 className="w-6 h-6 text-primary mb-2" />
            <h4 className="text-white font-medium">Audit Log</h4>
            <p className="text-zinc-500 text-sm">Cronologia operazioni</p>
          </a>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="card p-4">
          <p className="text-zinc-500">Versione Sistema</p>
          <p className="text-white font-mono">PServicePro v2.0.0</p>
        </div>
        <div className="card p-4">
          <p className="text-zinc-500">Ultimo Aggiornamento</p>
          <p className="text-white font-mono">{new Date().toLocaleString('it-IT')}</p>
        </div>
        <div className="card p-4">
          <p className="text-zinc-500">Stato Sistema</p>
          <p className="text-green-400 font-mono">● Operativo</p>
        </div>
      </div>

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Invia Comunicazione Broadcast
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Destinatari</label>
              <Select
                value={broadcastData.target_type}
                onValueChange={(v) => setBroadcastData({ ...broadcastData, target_type: v })}
              >
                <SelectTrigger data-testid="broadcast-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli utenti attivi</SelectItem>
                  <SelectItem value="ricambisti">Solo Ricambisti</SelectItem>
                  <SelectItem value="officine">Solo Officine</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Titolo</label>
              <Input
                value={broadcastData.title}
                onChange={(e) => setBroadcastData({ ...broadcastData, title: e.target.value })}
                placeholder="Es: Nuova promozione disponibile"
                data-testid="broadcast-title"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Messaggio</label>
              <textarea
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                rows={4}
                value={broadcastData.message}
                onChange={(e) => setBroadcastData({ ...broadcastData, message: e.target.value })}
                placeholder="Scrivi il messaggio da inviare..."
                data-testid="broadcast-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcastDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSendBroadcast} disabled={sending} data-testid="send-broadcast">
              {sending ? "Invio..." : "Invia Broadcast"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
